/**
 * CONTENIDO de la ayuda del portal del cliente.
 *
 * Dos consumidores, una sola fuente de verdad editorial:
 *   · CustomerHelpPage  → la guía global (/customer/help), con índice lateral y
 *     secciones ancladas. Explica el producto de principio a fin a alguien que
 *     entra por primera vez y no tiene a nadie que se lo cuente.
 *   · AccountHelpPanel  → la ayuda contextual «¿Cómo funciona?» de «Mi cuenta»,
 *     bloque a bloque (créditos, empleados, usuarios, almacenamiento), con un
 *     enlace a la sección de la guía que lo desarrolla.
 *
 * Bilingüe inline (es/en por bloque), igual que el manual del employee-portal
 * (pages/public/manual/manualContent.ts): el texto editorial LARGO vive aquí y
 * NO en los JSON de i18n — allí solo van los rótulos de UI (customer.help.*).
 * Meter párrafos de tres líneas en un JSON plano hace ilegible el contenido y
 * convierte cada corrección de estilo en un diff imposible de revisar.
 *
 * Lo que aquí se afirma está VERIFICADO contra el código que lo implementa
 * (backend/app/services/client_account_service.py y los diálogos de
 * CustomerAccountPage): las dos bolsas de créditos y su caducidad, el límite
 * mensual como tope de gasto —nunca una transferencia—, los dos volúmenes por
 * empleado y su crecimiento irreversible, y el reparto de responsabilidades
 * cuando la cuenta la gestiona un partner.
 */

export type Locale = 'es' | 'en';

export const resolveLocale = (lang?: string): Locale =>
  (lang || '').toLowerCase().startsWith('es') ? 'es' : 'en';

/**
 * AUDIENCIA de una sección o de un bloque suelto.
 *   · 'all'     → vale para cualquier cliente (por defecto si no se marca).
 *   · 'direct'  → SOLO cliente directo: contrata, paga y factura en este portal
 *                 (planes, pasarela, suscripción, comprar créditos…).
 *   · 'managed' → SOLO cuenta gestionada por un partner: es el partner quien
 *                 lleva créditos, cupo, espacio y facturación.
 *
 * La página filtra por aquí ANTES de pintar, así que los textos ya se redactan
 * en afirmativo —sin «si tu cuenta la gestiona… / si no…»—: cuando se muestran,
 * ya sabemos a quién van. Nunca conviven en pantalla las dos variantes.
 */
export type Audience = 'all' | 'direct' | 'managed';

/** ¿Debe verse este contenido? `managed` = la cuenta la lleva un partner. */
export const audienceMatches = (audience: Audience | undefined, managed: boolean): boolean =>
  !audience || audience === 'all' || audience === (managed ? 'managed' : 'direct');

/** Par «rótulo → explicación» de las listas y los pasos. */
export interface Item {
  k: string;
  v: string;
}

/** Un bloque de contenido. `audience` lo restringe a un tipo de cliente; si se
 *  omite, se muestra a todos. La audiencia es transversal a `kind`, de ahí la
 *  intersección (no rompe el discriminante `kind` del switch de la página). */
export type Block = (
  | { kind: 'p'; es: string; en: string }
  | { kind: 'h'; es: string; en: string }
  | { kind: 'tip'; es: string; en: string }
  | { kind: 'warn'; es: string; en: string }
  | { kind: 'list'; es: Item[]; en: Item[] }
  | { kind: 'steps'; es: Item[]; en: Item[] }
) & { audience?: Audience };

export type SectionId =
  | 'empieza'
  | 'empleado'
  | 'cuenta'
  | 'usuarios'
  | 'contratar'
  | 'creditos'
  | 'almacenamiento'
  | 'partner'
  | 'faq';

/** Icono de la sección. El nombre es abstracto a propósito: el contenido no
 *  importa MUI, la página traduce estas claves a componentes. */
export type SectionIcon =
  | 'start'
  | 'employee'
  | 'account'
  | 'users'
  | 'hire'
  | 'credits'
  | 'storage'
  | 'partner'
  | 'faq';

export interface Section {
  id: SectionId;
  icon: SectionIcon;
  /** Restringe la sección ENTERA a un tipo de cliente (por defecto 'all'). Una
   *  sección oculta no aparece ni en el cuerpo ni en el índice lateral. */
  audience?: Audience;
  es: { title: string; lead: string };
  en: { title: string; lead: string };
  blocks: Block[];
}

export const HELP_SECTIONS: Section[] = [
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'empieza',
    icon: 'start',
    es: {
      title: 'Empieza por aquí',
      lead: 'En diez minutos vas a entender todo el producto: qué es un empleado IA, qué es tu cuenta, cómo se contrata, qué consume y quién paga qué. No necesitas saber nada de tecnología.',
    },
    en: {
      title: 'Start here',
      lead: 'In ten minutes you will understand the whole product: what an AI employee is, what your account is, how you hire one, what it consumes and who pays for what. No technical background needed.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Has entrado en el portal desde el que se gobierna tu equipo de empleados IA. Aquí no se trabaja: aquí se decide quién trabaja, con qué medios y hasta dónde puede gastar. El trabajo del día a día ocurre en la aplicación de cada empleado, que se abre desde «Mi cuenta».',
        en: 'You have just entered the portal where your team of AI employees is governed. You do not work here: here you decide who works, with which resources and how much they may spend. The day-to-day work happens inside each employee’s own application, which you open from “My account”.',
      },
      {
        kind: 'h',
        es: 'El recorrido completo, en cuatro pasos',
        en: 'The whole journey, in four steps',
      },
      {
        kind: 'steps',
        es: [
          {
            k: 'Añade a tu equipo',
            v: 'Da de alta como usuarios a las personas que van a trabajar con los empleados. Sin al menos un usuario no se puede contratar nada: todo empleado necesita una persona que lo administre.',
          },
          {
            k: 'Contrata a tu primer empleado',
            v: 'Elígelo del catálogo, dile quién lo administra y con cuánto espacio nace. En unos minutos está listo.',
          },
          {
            k: 'Ábrelo y ponle a trabajar',
            v: 'Cuando aparezca como Activo, el botón «Abrir» te lleva a su aplicación. Ahí le hablas en lenguaje natural, le conectas sus herramientas y decides cuánta iniciativa tiene.',
          },
          {
            k: 'Vigila créditos y espacio',
            v: 'Los créditos son lo que gasta trabajando; el almacenamiento, donde guarda lo que produce. Las dos cifras están siempre a la vista en «Mi cuenta».',
          },
        ],
        en: [
          {
            k: 'Add your team',
            v: 'Register the people who will work with the employees as users. Without at least one user you cannot hire anything: every employee needs a person to administer it.',
          },
          {
            k: 'Hire your first employee',
            v: 'Pick it from the catalog, say who administers it and how much storage it is born with. It is ready in a few minutes.',
          },
          {
            k: 'Open it and put it to work',
            v: 'Once it shows as Active, the “Open” button takes you to its application. There you talk to it in plain language, connect its tools and decide how much initiative it has.',
          },
          {
            k: 'Keep an eye on credits and space',
            v: 'Credits are what it spends while working; storage is where it keeps what it produces. Both figures are always visible in “My account”.',
          },
        ],
      },
      {
        kind: 'tip',
        es: 'Esta guía está siempre a un clic: entrada «Ayuda» en el menú superior. Y en «Mi cuenta» tienes el botón «¿Cómo funciona?», que explica lo que estás viendo en esa pantalla sin salir de ella.',
        en: 'This guide is always one click away: the “Help” entry in the top menu. And in “My account” there is a “How does this work?” button that explains what you are looking at without leaving the screen.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'empleado',
    icon: 'employee',
    es: {
      title: 'Qué es un empleado IA (y qué no es)',
      lead: 'La palabra «empleado» no es marketing: es la forma más honesta de describir lo que contratas. No compras una licencia de software, incorporas a alguien que trabaja.',
    },
    en: {
      title: 'What an AI employee is (and what it is not)',
      lead: 'The word “employee” is not marketing: it is the most honest way to describe what you are hiring. You are not buying a software licence, you are onboarding someone who works.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Un empleado IA es un compañero de trabajo digital especializado en un oficio — atención a clientes, administración, análisis, ventas… — que observa tus sistemas, detecta lo que importa, propone acciones claras y las ejecuta con el permiso que le des. Se le habla en lenguaje natural, como a una persona nueva en el equipo.',
        en: 'An AI employee is a digital coworker specialized in a job — customer care, back office, analysis, sales… — that observes your systems, detects what matters, proposes clear actions and executes them with the permission you grant. You talk to it in plain language, the same way you would to a new teammate.',
      },
      { kind: 'h', es: 'Lo que sí es', en: 'What it is' },
      {
        kind: 'list',
        es: [
          {
            k: 'Trabaja, no solo responde',
            v: 'No espera a que le preguntes: revisa, cruza datos, detecta riesgos y avisa. Tú decides cuánta iniciativa le das.',
          },
          {
            k: 'Tiene su propio espacio',
            v: 'Su aplicación, sus datos y su almacenamiento son suyos. Lo que guarda un empleado no lo ve otro.',
          },
          {
            k: 'Tiene identidad y permisos',
            v: 'Se conecta a tus herramientas con una identidad concreta —la de su administrador— y solo llega hasta donde le hayas autorizado.',
          },
          {
            k: 'Deja traza de todo',
            v: 'Cada cosa que hace queda registrada en su aplicación: qué hizo, cuándo y con qué resultado.',
          },
        ],
        en: [
          {
            k: 'It works, it does not just answer',
            v: 'It does not wait to be asked: it reviews, cross-checks data, spots risks and warns you. You decide how much initiative it has.',
          },
          {
            k: 'It has its own space',
            v: 'Its application, its data and its storage belong to it. What one employee stores, another cannot see.',
          },
          {
            k: 'It has an identity and permissions',
            v: 'It connects to your tools with a specific identity — its administrator’s — and only reaches as far as you have authorized.',
          },
          {
            k: 'It leaves a trail',
            v: 'Everything it does is recorded inside its application: what it did, when and with what outcome.',
          },
        ],
      },
      { kind: 'h', es: 'Lo que no es', en: 'What it is not' },
      {
        kind: 'list',
        es: [
          {
            k: 'No es una app más',
            v: 'No es una pantalla donde tú haces el trabajo. El trabajo lo hace él; tú lo diriges y lo supervisas.',
          },
          {
            k: 'No es un chatbot suelto',
            v: 'No improvisa sobre un texto genérico: trabaja sobre tus datos, tus herramientas y tus reglas.',
          },
          {
            k: 'No es una licencia por puesto',
            v: 'No pagas por persona que lo usa: pagas por el trabajo que hace, medido en créditos.',
          },
          {
            k: 'No ve nada que no le conectes',
            v: 'Solo alcanza las herramientas y los datos que le hayas conectado explícitamente. Lo que no le des, no existe para él.',
          },
        ],
        en: [
          {
            k: 'It is not just another app',
            v: 'It is not a screen where you do the work. It does the work; you direct and supervise it.',
          },
          {
            k: 'It is not a loose chatbot',
            v: 'It does not improvise over generic text: it works on your data, your tools and your rules.',
          },
          {
            k: 'It is not a per-seat licence',
            v: 'You do not pay per person using it: you pay for the work it does, measured in credits.',
          },
          {
            k: 'It sees nothing you have not connected',
            v: 'It only reaches the tools and data you explicitly connected. Whatever you do not give it does not exist for it.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'Cada empleado del catálogo llega con su oficio aprendido: sabe qué mirar, qué preguntar y qué entregar en su especialidad. Puedes tener varios a la vez, cada uno con su administrador, su espacio y su límite de gasto.',
        en: 'Every employee in the catalog arrives with its trade already learned: it knows what to look at, what to ask and what to deliver in its specialty. You can have several at once, each with its own administrator, space and spending limit.',
      },
      {
        kind: 'tip',
        es: 'Antes de contratar, mira el catálogo: cada ficha cuenta qué hace ese empleado, para qué casos sirve y qué necesita tener conectado para trabajar. El enlace está dentro del propio diálogo de contratación.',
        en: 'Before hiring, browse the catalog: every listing explains what that employee does, which cases it covers and what it needs connected in order to work. The link sits inside the hiring dialog itself.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'cuenta',
    icon: 'account',
    es: {
      title: 'Tu cuenta y quién eres tú',
      lead: 'La cuenta es el marco de todo: dentro viven tus empleados, tus usuarios, tus créditos y tu almacenamiento. Y tú, en este portal, eres quien la gobierna.',
    },
    en: {
      title: 'Your account and who you are',
      lead: 'The account is the frame for everything: your employees, your users, your credits and your storage all live inside it. And you, in this portal, are the one who governs it.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Tu cuenta es el espacio contratado a tu nombre (o al de tu empresa). Todo lo que ves en «Mi cuenta» pertenece a ella y solo a ella: nada se comparte con otras cuentas de la plataforma.',
        en: 'Your account is the space contracted in your name (or your company’s). Everything you see in “My account” belongs to it and only to it: nothing is shared with other accounts on the platform.',
      },
      {
        kind: 'list',
        es: [
          { k: 'Cupo de empleados', v: 'Cuántos empleados IA puedes tener contratados a la vez.' },
          { k: 'Créditos', v: 'El saldo con el que trabajan. Se gasta con el uso real.' },
          { k: 'Almacenamiento', v: 'El espacio en disco que reparten tus empleados, con una parte incluida sin coste.' },
          { k: 'Usuarios', v: 'Las personas de tu equipo que pueden entrar a trabajar con los empleados.' },
        ],
        en: [
          { k: 'Employee quota', v: 'How many AI employees you may have hired at the same time.' },
          { k: 'Credits', v: 'The balance they work against. It is spent by actual usage.' },
          { k: 'Storage', v: 'The disk space your employees share, with a portion included at no cost.' },
          { k: 'Users', v: 'The people on your team who can log in and work with the employees.' },
        ],
      },
      { kind: 'h', es: 'Tú, el correo con el que entras aquí', en: 'You, and the email you log in with' },
      {
        kind: 'p',
        es: 'El correo con el que has entrado a este portal es de GESTIÓN. Sirve para contratar, dar de alta personas, poner límites y ver el consumo — pero no es una cuenta de trabajo: no entra a la aplicación de ningún empleado por el hecho de existir.',
        en: 'The email you used to log into this portal is a MANAGEMENT one. It is for hiring, registering people, setting limits and reviewing consumption — but it is not a working account: it does not get into any employee’s application just by existing.',
      },
      {
        kind: 'p',
        es: 'Si además de gestionar quieres trabajar con un empleado, date de alta a ti mismo como usuario y asígnate a él. Es la misma operación que harías con cualquier compañero, y evita la confusión clásica de «soy el dueño de la cuenta pero no puedo abrir el empleado».',
        en: 'If besides managing you also want to work with an employee, register yourself as a user and assign yourself to it. It is the same operation you would do for any teammate, and it avoids the classic confusion of “I own the account but I cannot open the employee”.',
      },
      {
        kind: 'tip',
        audience: 'managed',
        es: 'Tu cuenta la gestiona un partner: verás un aviso permanente con su nombre y su correo. Él lleva los créditos, el cupo, el espacio y la facturación — por eso en tu portal no hay planes ni pagos. Lo tienes desarrollado en la última sección de esta guía.',
        en: 'A partner manages your account: you will see a permanent notice with their name and email. They handle credits, quota, space and billing — that is why there are no plans or payments in your portal. The last section of this guide covers it in full.',
      },
      {
        kind: 'tip',
        audience: 'direct',
        es: 'Tu cuenta la llevas tú de principio a fin: la contratación de planes, la suscripción y la facturación viven en este mismo portal, en las secciones «Planes», «Suscripción» y «Facturación» del menú.',
        en: 'You run your account end to end: plan purchasing, your subscription and billing all live in this same portal, under the “Plans”, “Subscription” and “Billing” menu entries.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'usuarios',
    icon: 'users',
    es: {
      title: 'Usuarios: las personas que usarán los empleados',
      lead: 'Un usuario es una persona real de tu equipo. Los empleados no trabajan «para la cuenta» en abstracto: trabajan con personas, y cada persona entra con su propia identidad.',
    },
    en: {
      title: 'Users: the people who will work with the employees',
      lead: 'A user is a real person on your team. Employees do not work “for the account” in the abstract: they work with people, and each person logs in with their own identity.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Cada usuario tiene su nombre y su correo. Ese correo es su identidad de trabajo: con él entra a la aplicación de los empleados que le hayas asignado, y con él queda registrada su actividad.',
        en: 'Every user has a name and an email. That email is their working identity: they use it to enter the applications of the employees assigned to them, and their activity is recorded under it.',
      },
      { kind: 'h', es: 'Cómo entra cada persona', en: 'How each person logs in' },
      {
        kind: 'list',
        es: [
          {
            k: 'Contraseña',
            v: 'Se genera una contraseña de un solo uso. Te la mostramos UNA vez al crear el usuario y también se le envía por correo. Al entrar por primera vez, la plataforma le obliga a cambiarla.',
          },
          {
            k: 'Contraseña y Google',
            v: 'Puede entrar con esa contraseña o con su cuenta de Google, lo que le resulte más cómodo.',
          },
          {
            k: 'Solo Google',
            v: 'Entra con su cuenta de Google. No se genera contraseña y no hay nada que compartir ni que guardar.',
          },
        ],
        en: [
          {
            k: 'Password',
            v: 'A one-time password is generated. We show it to you ONCE when the user is created and it is also emailed to them. On first login the platform forces them to change it.',
          },
          {
            k: 'Password and Google',
            v: 'They can log in with that password or with their Google account, whichever suits them.',
          },
          {
            k: 'Google only',
            v: 'They log in with their Google account. No password is generated, so there is nothing to share or store.',
          },
        ],
      },
      {
        kind: 'warn',
        es: 'La contraseña generada se muestra una sola vez y no se puede volver a consultar. Cópiala antes de cerrar el aviso; si se pierde, la persona siempre podrá recuperarla desde el correo que recibió o pedir una nueva.',
        en: 'The generated password is shown only once and cannot be looked up again. Copy it before closing the notice; if it gets lost, the person can still use the email they received or ask for a new one.',
      },
      {
        kind: 'p',
        es: 'Si el correo que introduces ya existe en la plataforma, no se crea nadie nuevo: se reutiliza a esa persona y conserva su forma de entrar actual. Por eso en esos casos no aparece ninguna contraseña que copiar — no hace falta.',
        en: 'If the email you enter already exists on the platform, nobody new is created: that person is reused and keeps their current way of logging in. That is why no password appears to copy in those cases — none is needed.',
      },
      { kind: 'h', es: 'Asignar personas a empleados', en: 'Assigning people to employees' },
      {
        kind: 'p',
        es: 'Dar de alta a alguien no le da acceso a nada todavía: hay que asignarlo a los empleados con los que va a trabajar. Cada persona solo ve —y solo puede abrir— los empleados que tenga asignados.',
        en: 'Registering someone does not grant access to anything yet: you must assign them to the employees they will work with. Each person only sees — and can only open — the employees assigned to them.',
      },
      {
        kind: 'list',
        es: [
          { k: 'Miembro', v: 'Trabaja con el empleado en su día a día: le habla, le encarga tareas y recibe sus resultados.' },
          { k: 'Administrador', v: 'Además de trabajar, gobierna al empleado: sus conectores, su comportamiento y sus canales.' },
        ],
        en: [
          { k: 'Member', v: 'Works with the employee day to day: talks to it, assigns tasks and receives its results.' },
          { k: 'Administrator', v: 'Besides working, governs the employee: its connectors, its behavior and its channels.' },
        ],
      },
      {
        kind: 'tip',
        es: 'Quitar una asignación es inmediato y no borra a la persona: simplemente deja de tener acceso a ese empleado. Es la forma limpia de gestionar una baja o un cambio de responsabilidades.',
        en: 'Removing an assignment is immediate and does not delete the person: they simply lose access to that employee. It is the clean way to handle someone leaving or changing role.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'contratar',
    icon: 'hire',
    es: {
      title: 'Contratar un empleado, paso a paso',
      lead: 'Contratar es tomar cuatro decisiones: si te queda cupo, a quién contratas, quién lo administra y con cuánto espacio nace. Ninguna requiere conocimientos técnicos, pero conviene entenderlas.',
    },
    en: {
      title: 'Hiring an employee, step by step',
      lead: 'Hiring means making four decisions: whether you have quota left, whom you hire, who administers it and how much space it is born with. None of them requires technical knowledge, but they are worth understanding.',
    },
    blocks: [
      { kind: 'h', es: '1 · El cupo', en: '1 · The quota' },
      {
        kind: 'p',
        es: 'Tu cuenta tiene un cupo: el número máximo de empleados que puedes tener contratados a la vez. Lo ves en «Mis empleados» como «Cupo: usados / total». Si está lleno, el botón de contratar se desactiva y hay que ampliar el cupo con quien te lleva la cuenta.',
        en: 'Your account has a quota: the maximum number of employees you may have hired at the same time. You see it under “My employees” as “Quota: used / total”. When it is full the hire button is disabled and the quota has to be raised by whoever manages your account.',
      },
      {
        kind: 'p',
        es: 'Eliminar un empleado libera su plaza inmediatamente, pero retira también su aplicación y sus datos: no es una pausa, es una baja definitiva.',
        en: 'Removing an employee frees its slot immediately, but it also withdraws its application and its data: it is not a pause, it is a permanent termination.',
      },
      { kind: 'h', es: '2 · A quién contratas', en: '2 · Whom you hire' },
      {
        kind: 'p',
        es: 'El selector solo muestra nombres, así que la decisión se toma en el catálogo: ahí está la ficha de cada empleado con lo que hace, sus casos de uso y lo que necesita tener conectado. El enlace al catálogo está en el propio diálogo de contratación.',
        en: 'The selector only shows names, so the decision is made in the catalog: that is where each employee’s listing explains what it does, its use cases and what it needs connected. The link to the catalog is inside the hiring dialog itself.',
      },
      {
        kind: 'p',
        es: 'Puedes darle un nombre para mostrar («Marta, atención a clientes») si prefieres llamarlo como llamarías a un compañero. Si lo dejas vacío, se usa el nombre del catálogo.',
        en: 'You can give it a display name (“Martha, customer care”) if you prefer to call it the way you would call a teammate. Leave it empty and the catalog name is used.',
      },
      { kind: 'h', es: '3 · Su administrador (y su identidad)', en: '3 · Its administrator (and its identity)' },
      {
        kind: 'p',
        es: 'Todo empleado necesita un administrador, que se elige entre los usuarios de tu cuenta. Es la decisión más importante del alta, porque cumple dos papeles a la vez: administra al empleado (sus conectores, su comportamiento, sus canales) y es la IDENTIDAD con la que ese empleado trabaja en la plataforma.',
        en: 'Every employee needs an administrator, chosen among the users of your account. It is the most important decision at hiring time, because it plays two roles at once: it administers the employee (its connectors, behavior and channels) and it is the IDENTITY the employee works with on the platform.',
      },
      {
        kind: 'p',
        es: 'En la práctica: cuando el empleado se conecte a un correo, un CRM o un calendario, lo hará con la cuenta de esa persona. Elige a alguien que de verdad tenga acceso a las herramientas donde el empleado va a trabajar; si no hay ningún usuario todavía, hay que crear uno antes de poder contratar.',
        en: 'In practice: when the employee connects to a mailbox, a CRM or a calendar, it does so with that person’s account. Pick someone who genuinely has access to the tools the employee will work in; if there is no user yet, one has to be created before you can hire.',
      },
      { kind: 'h', es: '4 · Su almacenamiento', en: '4 · Its storage' },
      {
        kind: 'p',
        es: 'Al contratar eliges el tamaño de sus dos discos. Se decide aquí, y no después, por una razón muy concreta: nacer con el tamaño adecuado no cuesta nada, mientras que ampliar más tarde es permanente y puede generar cargo. La pantalla te muestra en vivo cuánto ocupa, cuánto se come de lo incluido y si sumaría coste.',
        en: 'When hiring you choose the size of its two disks. This is decided here and not later for a very concrete reason: being born with the right size costs nothing, while expanding later is permanent and may generate a charge. The screen shows you live how much it takes, how much of the included space it eats and whether it would add a cost.',
      },
      { kind: 'h', es: 'Opcional · Su límite mensual', en: 'Optional · Its monthly limit' },
      {
        kind: 'p',
        es: 'Puedes ponerle un tope de gasto para una ventana de 30 días. No es una transferencia de saldo ni un monedero aparte: es simplemente el máximo que ese empleado puede consumir en ese periodo. Déjalo vacío o a 0 y trabajará sin límite propio, contra el saldo común de la cuenta.',
        en: 'You can set a spending cap for a rolling 30-day window. It is not a balance transfer nor a separate wallet: it is simply the maximum that employee may consume in that period. Leave it empty or at 0 and it works with no cap of its own, against the account’s shared balance.',
      },
      {
        kind: 'p',
        es: 'Al confirmar, el empleado tarda unos minutos en prepararse: verás su estado cambiar hasta «Activo», y entonces el botón «Abrir» lleva a su aplicación. Si tarda más de lo esperado, recarga la página antes de preocuparte.',
        en: 'After confirming, the employee takes a few minutes to get ready: you will see its status change until “Active”, and then the “Open” button takes you to its application. If it takes longer than expected, reload the page before worrying.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'creditos',
    icon: 'credits',
    es: {
      title: 'Créditos: la gasolina de tus empleados',
      lead: 'Un crédito es la unidad con la que se mide el trabajo de un empleado. No pagas por persona ni por pantalla abierta: pagas por lo que trabajan, y solo por eso.',
    },
    en: {
      title: 'Credits: the fuel your employees run on',
      lead: 'A credit is the unit that measures an employee’s work. You do not pay per person or per open screen: you pay for the work done, and for nothing else.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Cada vez que un empleado lee, razona, redacta o ejecuta algo, consume una parte de su saldo. Una consulta corta gasta poquísimo; un análisis largo sobre miles de registros gasta más. El saldo baja SOLO con ese consumo real: nada lo reserva, lo bloquea ni lo aparta.',
        en: 'Every time an employee reads, reasons, writes or executes something, it consumes part of the balance. A short question costs very little; a long analysis over thousands of records costs more. The balance only goes down with that real consumption: nothing reserves, blocks or sets it aside.',
      },
      { kind: 'h', es: 'Dos bolsas: la del plan y la extra', en: 'Two pots: the plan one and the extra one' },
      {
        kind: 'list',
        es: [
          {
            k: 'Créditos del plan',
            v: 'Son los que te corresponden por tu plan y se renuevan al empezar cada ciclo. Lo que no hayas gastado al terminar el ciclo CADUCA: no se acumula para el mes siguiente.',
          },
          {
            k: 'Créditos extra',
            v: 'Son las recargas y los ajustes puntuales. NO caducan: se quedan en tu cuenta hasta que se consumen.',
          },
        ],
        en: [
          {
            k: 'Plan credits',
            v: 'These come with your plan and are renewed at the start of each cycle. Whatever you have not spent when the cycle ends EXPIRES: it does not roll over to the next month.',
          },
          {
            k: 'Extra credits',
            v: 'These are top-ups and one-off adjustments. They do NOT expire: they stay in your account until they are consumed.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'El consumo gasta siempre PRIMERO la bolsa del plan —la que caduca— y solo cuando esa se agota tira de la extra. Es lo que más te conviene: así lo que iba a caducar se aprovecha antes que lo que no caduca. En «Mi cuenta» ves las dos por separado y, destacado, el «Disponible total» que suma ambas.',
        en: 'Consumption always drains the plan pot FIRST — the one that expires — and only pulls from the extra pot once the plan one is empty. That is in your favour: what was going to expire gets used before what never expires. In “My account” you see both separately plus the highlighted “Total available”, which adds them up.',
      },
      { kind: 'h', es: 'El límite mensual de cada empleado', en: 'Each employee’s monthly limit' },
      {
        kind: 'p',
        es: 'Es un TOPE DE GASTO en una ventana de 30 días, no una transferencia. Ponerle 500 créditos de límite a un empleado no le entrega 500 créditos ni se los quita a nadie: solo significa «no gastes más de 500 en 30 días». El saldo de la cuenta sigue siendo uno y solo baja con el consumo real.',
        en: 'It is a SPENDING CAP over a rolling 30-day window, not a transfer. Setting a 500-credit limit on an employee does not hand it 500 credits nor take them from anyone else: it only means “do not spend more than 500 in 30 days”. The account balance stays single and only drops with real consumption.',
      },
      {
        kind: 'p',
        es: 'De ahí una consecuencia que sorprende al principio: la suma de los límites de tus empleados PUEDE superar tu saldo, y no pasa nada. Son dos cosas distintas — el reparto de responsabilidad y el dinero disponible. El límite sirve para que un empleado no se coma él solo la cuenta.',
        en: 'Hence something that surprises people at first: the sum of your employees’ limits CAN exceed your balance, and that is fine. They are two different things — the split of responsibility and the money available. The limit exists so that one employee cannot eat the whole account on its own.',
      },
      { kind: 'h', es: 'Qué pasa cuando se agotan', en: 'What happens when they run out' },
      {
        kind: 'p',
        es: 'Cuando el disponible llega a cero, los empleados dejan de trabajar. No se borra nada, no se pierde su configuración ni sus datos: simplemente se quedan parados hasta que entren créditos nuevos, ya sea por la renovación del plan o por una recarga. En cuanto hay saldo, siguen donde lo dejaron.',
        en: 'When the available balance hits zero, the employees stop working. Nothing is deleted, no configuration or data is lost: they simply stand still until new credits arrive, either from the plan renewal or from a top-up. As soon as there is balance, they pick up where they left off.',
      },
      {
        kind: 'tip',
        es: 'No hace falta que vigiles la cifra: cuando el saldo baja del 10 % del ciclo aparece un aviso ámbar en «Mi cuenta» diciéndote a quién pedir más. Es el momento de actuar, no cuando ya está a cero.',
        en: 'You do not need to watch the number: when the balance drops below 10 % of the cycle an amber notice appears in “My account” telling you whom to ask for more. That is the moment to act, not when it already reads zero.',
      },
      { kind: 'h', es: 'Cómo consigues más créditos', en: 'How you get more credits' },
      {
        kind: 'p',
        audience: 'direct',
        es: 'Cuando necesites más, ve a «Planes»: puedes pasar a un plan mensual mayor —más créditos cada ciclo— o comprar un pack extra, que no caduca. El pago se hace en el propio portal y el saldo entra al momento.',
        en: 'When you need more, go to “Plans”: you can move to a bigger monthly plan — more credits each cycle — or buy an extra pack, which never expires. Payment happens inside the portal and the balance arrives at once.',
      },
      {
        kind: 'p',
        audience: 'managed',
        es: 'Cuando necesites más, escríbele a tu partner: son ellos quienes gestionan tu saldo y las recargas. En tu portal no se compran créditos ni hay pasarela de pago — el aviso de saldo bajo te recuerda a quién dirigirte.',
        en: 'When you need more, write to your partner: they are the ones who manage your balance and top-ups. Your portal has no credit purchase and no payment gateway — the low-balance notice reminds you whom to reach out to.',
      },
      {
        kind: 'p',
        es: 'El consumo se sincroniza periódicamente desde la aplicación de cada empleado, así que puede ir unos minutos por detrás de la realidad. La sección «Créditos» te dice cuándo se actualizó por última vez y tiene un botón para forzar la actualización, además del histórico de movimientos filtrable por ciclo, últimos 30 días o todo.',
        en: 'Consumption is synchronized periodically from each employee’s application, so it may lag reality by a few minutes. The “Credits” section tells you when it was last refreshed and has a button to force an update, plus the movement history filtered by cycle, last 30 days or everything.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'almacenamiento',
    icon: 'storage',
    es: {
      title: 'Almacenamiento: dónde vive lo que hacen',
      lead: 'Tus empleados producen y guardan cosas. Ese espacio se contrata, se reparte y —esto es lo importante— solo puede crecer.',
    },
    en: {
      title: 'Storage: where what they do actually lives',
      lead: 'Your employees produce and store things. That space is contracted, shared and — this is the key part — can only grow.',
    },
    blocks: [
      { kind: 'h', es: 'Dos volúmenes por empleado', en: 'Two volumes per employee' },
      {
        kind: 'list',
        es: [
          {
            k: 'Espacio de trabajo del agente',
            v: 'Donde el empleado guarda lo que produce mientras trabaja: informes, ficheros generados, material de apoyo.',
          },
          {
            k: 'Datos de la aplicación',
            v: 'Donde vive lo propio de su aplicación: su histórico, su configuración, su memoria de trabajo.',
          },
        ],
        en: [
          {
            k: 'Agent workspace',
            v: 'Where the employee stores what it produces while working: reports, generated files, supporting material.',
          },
          {
            k: 'Application data',
            v: 'Where the application’s own material lives: its history, its configuration, its working memory.',
          },
        ],
      },
      {
        kind: 'p',
        es: 'Los dos volúmenes son de ese empleado y de nadie más. Al sumar los de todos tus empleados sale el uso de la cuenta, que es lo que ves en la tarjeta «Almacenamiento» de «Mi cuenta».',
        en: 'Both volumes belong to that employee and to nobody else. Adding up those of all your employees gives the account usage, which is what the “Storage” card in “My account” shows.',
      },
      { kind: 'h', audience: 'direct', es: 'Lo incluido y lo que se cobra', en: 'What is included and what is charged' },
      {
        kind: 'p',
        audience: 'direct',
        es: 'Tu cuenta trae una cantidad de espacio incluida sin coste. Mientras la suma de tus empleados quepa ahí, el almacenamiento no genera ningún cargo. El espacio que exceda lo incluido sí tiene un coste por ciclo, y se mantiene mientras ese espacio siga existiendo.',
        en: 'Your account comes with an amount of space included at no cost. As long as your employees together fit inside it, storage generates no charge. Space beyond what is included does have a per-cycle cost, and it stays as long as that space exists.',
      },
      { kind: 'h', audience: 'managed', es: 'Lo incluido y quién lo lleva', en: 'What is included and who handles it' },
      {
        kind: 'p',
        audience: 'managed',
        es: 'Tu cuenta trae una cantidad de espacio incluida. Mientras la suma de tus empleados quepa ahí, no tienes que hacer nada. El espacio de tu cuenta —y su ampliación— forma parte de lo que tiene contratado tu partner: por eso aquí no verás precios de disco. Si necesitas más, es a él a quien se lo pides.',
        en: 'Your account comes with an amount of space included. As long as your employees together fit inside it, there is nothing for you to do. Your account’s space — and any expansion of it — is part of what your partner has contracted: that is why you will not see disk prices here. If you need more, they are the ones to ask.',
      },
      {
        kind: 'p',
        es: 'Además puede haber un techo de cuenta: un máximo total que no se puede rebasar. Si lo alcanzas, no podrás contratar ni ampliar hasta que se suba, y la propia pantalla te lo dice antes de dejarte rellenar el formulario.',
        en: 'There may also be an account ceiling: a total maximum that cannot be exceeded. If you hit it, you will not be able to hire or expand until it is raised, and the screen tells you so before letting you fill in the form.',
      },
      { kind: 'h', es: 'Se elige al contratar, se amplía después', en: 'Chosen at hiring time, expanded later' },
      {
        kind: 'p',
        es: 'El tamaño inicial se decide en el momento de contratar. Después puedes ampliarlo cuando quieras, en tramos, eligiendo dónde cae el espacio nuevo: a partes iguales entre los dos volúmenes, todo al espacio de trabajo o todo a los datos.',
        en: 'The initial size is decided at hiring time. Afterwards you can expand it whenever you want, in steps, choosing where the new space lands: split evenly between both volumes, all to the workspace or all to the data.',
      },
      {
        kind: 'warn',
        es: 'El almacenamiento SOLO puede crecer. Un disco no se puede reducir después: encogerlo obligaría a recrearlo y se perderían sus datos. La única forma de liberar espacio es retirar al empleado entero. Por eso conviene nacer con el tamaño justo y ampliar cuando de verdad haga falta.',
        en: 'Storage can ONLY grow. A disk cannot be shrunk afterwards: shrinking it would mean recreating it and losing its data. The only way to free space is to remove the whole employee. That is why it pays to start with the right size and expand only when genuinely needed.',
      },
      {
        kind: 'p',
        es: 'Al ampliar, el empleado se vuelve a desplegar para que el disco crezca de verdad: puede quedarse unos segundos sin responder. Si en ese momento está parado, estrenará el tamaño nuevo en su siguiente arranque.',
        en: 'When you expand, the employee is redeployed so the disk actually grows: it may go a few seconds without answering. If it happens to be stopped, it will pick up the new size on its next start.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'partner',
    icon: 'partner',
    audience: 'managed',
    es: {
      title: 'Tu cuenta la gestiona un partner',
      lead: 'Tu cuenta llega de la mano de un partner: la empresa que te ha traído a WAI y que se ocupa de la parte administrativa. Esto es lo que cambia contigo — y todo para simplificarte la vida.',
    },
    en: {
      title: 'A partner manages your account',
      lead: 'Your account arrives through a partner: the company that brought you to WAI and takes care of the administrative side. Here is what that changes for you — all of it to make your life simpler.',
    },
    blocks: [
      {
        kind: 'p',
        es: 'Lo sabrás porque en la parte superior del portal, y también dentro de «Mi cuenta», aparece un aviso permanente con el nombre de tu partner y su correo de contacto. No es un detalle decorativo: es a quién escribir cuando necesitas algo.',
        en: 'You will know because a permanent notice with your partner’s name and contact email appears at the top of the portal and inside “My account”. It is not decoration: it is whom to write to whenever you need something.',
      },
      { kind: 'h', es: 'De qué se ocupa tu partner', en: 'What your partner takes care of' },
      {
        kind: 'list',
        es: [
          { k: 'Los créditos', v: 'Tu plan mensual y cualquier recarga extra salen de lo que él tiene contratado.' },
          { k: 'El cupo de empleados', v: 'Ampliar el número de empleados que puedes tener a la vez se pide a él.' },
          { k: 'El almacenamiento', v: 'El espacio de tu cuenta forma parte de lo suyo; por eso no verás precios de disco.' },
          { k: 'La facturación y el contrato', v: 'La relación económica es con él: en tu portal no hay planes, pagos ni facturas.' },
          { k: 'La primera línea de ayuda', v: 'Para dudas de negocio o de uso, él es tu interlocutor natural.' },
        ],
        en: [
          { k: 'Credits', v: 'Your monthly plan and any extra top-up come out of what they have contracted.' },
          { k: 'The employee quota', v: 'Raising how many employees you may have at once is requested from them.' },
          { k: 'Storage', v: 'Your account’s space is part of theirs; that is why you will not see disk prices.' },
          { k: 'Billing and the contract', v: 'The commercial relationship is with them: your portal has no plans, payments or invoices.' },
          { k: 'First-line support', v: 'For business or usage questions they are your natural point of contact.' },
        ],
      },
      {
        kind: 'p',
        es: 'Lo que sigue siendo tuyo, y solo tuyo: contratar y retirar empleados dentro de tu cupo, dar de alta a tu equipo, asignar personas, poner límites de gasto y ampliar el espacio de un empleado. El gobierno del día a día no se delega.',
        en: 'What remains yours, and only yours: hiring and removing employees within your quota, registering your team, assigning people, setting spending limits and expanding an employee’s space. Day-to-day governance is not delegated.',
      },
      {
        kind: 'tip',
        es: 'Para cualquier duda de negocio, una recarga de créditos o ampliar tu cupo de empleados, tu contacto es tu partner: escríbele al correo del aviso. Te atienden ellos directamente.',
        en: 'For any business question, a credit top-up or raising your employee quota, your point of contact is your partner: write to the email in the notice. They attend to you directly.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'faq',
    icon: 'faq',
    es: {
      title: 'Preguntas rápidas',
      lead: 'Las dudas que aparecen siempre en la primera semana, contestadas en una línea.',
    },
    en: {
      title: 'Quick questions',
      lead: 'The doubts that always show up during the first week, answered in one line.',
    },
    blocks: [
      {
        kind: 'list',
        es: [
          {
            k: 'He creado un usuario y no puede abrir el empleado',
            v: 'Falta asignarlo. Dar de alta a alguien no da acceso: hay que asignarlo al empleado desde la fila del usuario.',
          },
          {
            k: 'He perdido la contraseña que salió al crear un usuario',
            v: 'No se puede volver a ver, pero también se envió por correo a esa persona. Si tampoco lo tiene, vuelve a darle acceso.',
          },
          {
            k: '¿Por qué mi correo del portal no abre ningún empleado?',
            v: 'Porque es de gestión. Date de alta como usuario y asígnate al empleado que quieras usar.',
          },
          {
            k: 'He puesto un límite mensual y mi saldo no ha bajado',
            v: 'Correcto: el límite es un tope de gasto, no una transferencia. El saldo solo baja con el consumo real.',
          },
          {
            k: '¿Puedo reducir el disco de un empleado?',
            v: 'No. El almacenamiento solo crece; para liberar espacio hay que retirar al empleado.',
          },
          {
            k: 'Mi empleado lleva minutos «preparándose»',
            v: 'Es normal en el alta y tras ampliar el disco. Recarga la página; si pasa mucho tiempo, escribe a quien te lleva la cuenta.',
          },
          {
            k: '¿Se pierde algo si me quedo sin créditos?',
            v: 'No. Los empleados se paran y siguen exactamente donde estaban cuando vuelve a haber saldo.',
          },
          {
            k: '¿Los créditos del plan se acumulan?',
            v: 'No: lo que no gastes en el ciclo caduca al renovarse. Los créditos extra, en cambio, no caducan.',
          },
        ],
        en: [
          {
            k: 'I created a user and they cannot open the employee',
            v: 'The assignment is missing. Registering someone grants no access: assign them to the employee from the user’s row.',
          },
          {
            k: 'I lost the password shown when creating a user',
            v: 'It cannot be shown again, but it was also emailed to that person. If they do not have it either, give them access again.',
          },
          {
            k: 'Why does my portal email not open any employee?',
            v: 'Because it is a management email. Register yourself as a user and assign yourself to the employee you want to use.',
          },
          {
            k: 'I set a monthly limit and my balance did not drop',
            v: 'That is correct: the limit is a spending cap, not a transfer. The balance only drops with real consumption.',
          },
          {
            k: 'Can I shrink an employee’s disk?',
            v: 'No. Storage only grows; to free space you have to remove the employee.',
          },
          {
            k: 'My employee has been “getting ready” for minutes',
            v: 'That is normal at hiring time and after expanding the disk. Reload the page; if it takes very long, contact whoever manages your account.',
          },
          {
            k: 'Do I lose anything if I run out of credits?',
            v: 'No. The employees stop and resume exactly where they were once there is balance again.',
          },
          {
            k: 'Do plan credits roll over?',
            v: 'No: whatever you do not spend in the cycle expires when it renews. Extra credits, on the other hand, never expire.',
          },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════
// AYUDA CONTEXTUAL de «Mi cuenta» — un bloque por bloque de la pantalla.
// Cada uno explica lo que se está viendo y enlaza a la sección de la guía
// global que lo desarrolla, para que la ayuda corta no tenga que repetirlo.
// ══════════════════════════════════════════════════════════════════════════

export type HelpBlockId = 'credits' | 'employees' | 'users' | 'storage' | 'packages';

/** Orden canónico: el mismo en el que se leen los bloques de «Mi cuenta». */
export const ACCOUNT_HELP_ORDER: HelpBlockId[] = ['credits', 'employees', 'users', 'storage'];

/** Una viñeta de la ayuda contextual: bilingüe y con audiencia opcional, para
 *  que el mismo bloque diga «compra en Planes» al cliente directo y «pídeselo a
 *  tu partner» al gestionado sin que convivan las dos frases. */
export interface AccountHelpPoint {
  es: string;
  en: string;
  audience?: Audience;
}

export interface AccountHelpBlock {
  id: HelpBlockId;
  icon: SectionIcon;
  /** Sección de la guía global que desarrolla este bloque. */
  section: SectionId;
  es: { title: string; lead: string };
  en: { title: string; lead: string };
  /** Viñetas del bloque; se filtran por audiencia en el momento de pintar. */
  points: AccountHelpPoint[];
}

export const ACCOUNT_HELP: Record<HelpBlockId, AccountHelpBlock> = {
  packages: {
    id: 'packages',
    icon: 'credits',
    section: 'creditos',
    // Solo se muestra en «Planes», una página exclusiva del cliente directo:
    // por eso todo su contenido asume que aquí se compra.
    es: {
      title: 'Paquetes de créditos',
      lead: 'Lo único que se compra aquí son créditos: el combustible con el que trabajan tus empleados.',
    },
    en: {
      title: 'Credit packages',
      lead: 'The only thing you buy here is credits: the fuel your employees work on.',
    },
    points: [
      {
        es: 'El PLAN mensual se renueva cada ciclo y lo que no gastes caduca al cerrarlo: elige el tamaño que de verdad vayas a usar, no el más grande.',
        en: 'The monthly PLAN renews every cycle and whatever you do not spend expires when it closes: pick the size you will actually use, not the biggest one.',
      },
      {
        es: 'Los PACKS EXTRA son una compra puntual y NO caducan; por eso salen algo más caros por crédito que el plan.',
        en: 'EXTRA PACKS are a one-off purchase and never expire; that is why they cost slightly more per credit than the plan.',
      },
      {
        es: 'Tu consumo gasta primero los créditos del plan (los que iban a caducar) y después los extra.',
        en: 'Your usage spends plan credits first (the ones about to expire) and extra credits afterwards.',
      },
      {
        es: 'Cuanto mayor es el paquete de plan, más barato sale el crédito: el ahorro aparece en cada tarjeta.',
        en: 'The larger the plan package, the cheaper each credit gets: the saving is shown on every card.',
      },
      {
        es: 'El texto de cada paquete te dice para qué da de sí en trabajo real, no en cifras técnicas.',
        en: 'Each package’s text tells you what it covers in real work, not in technical figures.',
      },
    ],
  },
  credits: {
    id: 'credits',
    icon: 'credits',
    section: 'creditos',
    es: {
      title: 'Créditos',
      lead: 'Es el saldo con el que trabajan tus empleados. Baja únicamente con el consumo real: nada lo reserva por adelantado.',
    },
    en: {
      title: 'Credits',
      lead: 'This is the balance your employees work against. It only drops with real consumption: nothing is reserved up front.',
    },
    points: [
      {
        es: 'La cifra grande es el «Disponible total»: los créditos del plan que te quedan más tus créditos extra.',
        en: 'The big figure is the “Total available”: your remaining plan credits plus your extra credits.',
      },
      {
        es: 'Los del plan se renuevan cada ciclo y lo que no gastes caduca; los extra no caducan nunca.',
        en: 'Plan credits renew every cycle and whatever you do not spend expires; extra credits never expire.',
      },
      {
        es: 'El consumo gasta primero la bolsa que caduca, así aprovechas antes lo que ibas a perder.',
        en: 'Consumption drains the expiring pot first, so you use up what you were going to lose anyway.',
      },
      {
        es: 'El histórico de movimientos se filtra por ciclo, últimos 30 días o todo, y el consumo se sincroniza cada poco (puedes forzarlo con «Actualizar»).',
        en: 'The movement history can be filtered by cycle, last 30 days or everything, and consumption syncs regularly (force it with “Refresh”).',
      },
      {
        es: 'Si el saldo llega a cero los empleados se paran, pero no se pierde nada: al recargar siguen donde estaban.',
        en: 'If the balance reaches zero the employees stop, but nothing is lost: after a top-up they resume where they were.',
      },
      {
        audience: 'direct',
        es: 'Para sumar créditos vas a «Planes»: un plan mensual mayor o un pack extra que no caduca; el pago se hace aquí y el saldo entra al momento.',
        en: 'To add credits you go to “Plans”: a bigger monthly plan or an extra pack that never expires; payment happens here and the balance arrives at once.',
      },
      {
        audience: 'managed',
        es: 'Para sumar créditos se lo pides a tu partner: ellos llevan tu saldo y las recargas. Aquí no se compran créditos ni hay pasarela de pago.',
        en: 'To add credits you ask your partner: they manage your balance and top-ups. There is no credit purchase or payment gateway here.',
      },
    ],
  },
  employees: {
    id: 'employees',
    icon: 'employee',
    section: 'contratar',
    es: {
      title: 'Mis empleados',
      lead: 'Cada tarjeta es un empleado IA contratado, con su estado, su administrador, su consumo y su espacio.',
    },
    en: {
      title: 'My employees',
      lead: 'Each card is a hired AI employee, with its status, its administrator, its consumption and its space.',
    },
    points: [
      {
        es: '«Cupo: usados / total» es cuántos empleados puedes tener a la vez; si está lleno, no se puede contratar hasta liberar una plaza o ampliarlo.',
        en: '“Quota: used / total” is how many employees you may have at once; when it is full you cannot hire until a slot is freed or the quota is raised.',
      },
      {
        es: 'El administrador es un usuario de tu cuenta: gobierna al empleado y es además la identidad con la que este trabaja en tus herramientas.',
        en: 'The administrator is a user of your account: it governs the employee and is also the identity it works with in your tools.',
      },
      {
        es: '«Límite mensual» es un tope de gasto en 30 días, no una transferencia: la suma de límites puede superar tu saldo sin problema.',
        en: '“Monthly limit” is a 30-day spending cap, not a transfer: the sum of all limits may exceed your balance with no problem.',
      },
      {
        es: '«Abrir» lleva a la aplicación del empleado, donde ocurre el trabajo de verdad; solo aparece cuando está Activo.',
        en: '“Open” takes you to the employee’s application, where the real work happens; it only appears once it is Active.',
      },
      {
        es: 'Eliminar retira su aplicación y sus datos y libera su plaza del cupo: no es una pausa.',
        en: 'Removing withdraws its application and its data and frees its quota slot: it is not a pause.',
      },
    ],
  },
  users: {
    id: 'users',
    icon: 'users',
    section: 'usuarios',
    es: {
      title: 'Usuarios',
      lead: 'Son las personas reales de tu equipo que van a trabajar con los empleados. El correo del portal, el tuyo, es solo de gestión.',
    },
    en: {
      title: 'Users',
      lead: 'These are the real people on your team who will work with the employees. The portal email — yours — is for management only.',
    },
    points: [
      {
        es: 'Cada persona entra con su propio correo: con contraseña, con contraseña y Google, o solo con Google.',
        en: 'Each person logs in with their own email: with a password, with password and Google, or with Google only.',
      },
      {
        es: 'La contraseña generada se muestra UNA sola vez —cópiala— y también se envía por correo; al entrar, la plataforma obliga a cambiarla.',
        en: 'The generated password is shown ONCE — copy it — and is also emailed; on first login the platform forces a change.',
      },
      {
        es: 'Si el correo ya existía en la plataforma se reutiliza a esa persona con su forma de entrar actual, y no hay contraseña que enseñar.',
        en: 'If the email already existed on the platform, that person is reused with their current login method and there is no password to show.',
      },
      {
        es: 'Dar de alta no da acceso: hay que asignar a la persona a los empleados con los que va a trabajar, como Miembro o Administrador.',
        en: 'Registering grants no access: you must assign the person to the employees they will work with, as Member or Administrator.',
      },
      {
        es: 'Quitar una asignación es inmediato y no borra a la persona: solo deja de ver ese empleado.',
        en: 'Removing an assignment is immediate and does not delete the person: they simply stop seeing that employee.',
      },
    ],
  },
  storage: {
    id: 'storage',
    icon: 'storage',
    section: 'almacenamiento',
    es: {
      title: 'Almacenamiento',
      lead: 'El espacio en disco que ocupan tus empleados. Cada uno tiene dos volúmenes: su espacio de trabajo y los datos de su aplicación.',
    },
    en: {
      title: 'Storage',
      lead: 'The disk space your employees occupy. Each one has two volumes: its workspace and its application data.',
    },
    points: [
      {
        es: 'La tarjeta muestra lo que usas y cuánto viene incluido sin coste en tu cuenta.',
        en: 'The card shows how much you use and how much comes included at no cost in your account.',
      },
      {
        es: 'El tamaño se elige al contratar —nacer bien dimensionado no cuesta nada— y se amplía después en tramos.',
        en: 'The size is chosen at hiring time — being born well sized costs nothing — and expanded later in steps.',
      },
      {
        es: 'El almacenamiento SOLO puede crecer: un disco no se puede reducir, y solo retirar al empleado libera su espacio.',
        en: 'Storage can ONLY grow: a disk cannot be shrunk, and only removing the employee frees its space.',
      },
      {
        audience: 'direct',
        es: 'El espacio que exceda lo incluido tiene un coste por ciclo mientras siga existiendo.',
        en: 'Space beyond what is included has a per-cycle cost for as long as it exists.',
      },
      {
        audience: 'managed',
        es: 'El espacio de tu cuenta lo lleva tu partner; por eso aquí no verás precios de disco. Si necesitas más, se lo pides a él.',
        en: 'Your account’s space is handled by your partner; that is why you will not see disk prices here. If you need more, you ask them.',
      },
      {
        es: 'Al ampliar, el empleado se vuelve a desplegar y puede quedarse unos segundos sin responder.',
        en: 'When expanding, the employee is redeployed and may go a few seconds without answering.',
      },
    ],
  },
};
